import { addError } from "@wayflyer/blackbox-fe";
import { LandingLayout, PageGridCell, PageGridRow, Spinner } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { useCreateFundingApplicationMutation } from "../../queries/funding-application/queries";
import { useInReviewDetails } from "../../queries/review-screen/review-screen-queries";

import { ApplicationStatusCard, type ApplicationStatusCardProps } from "./application-status-card";
import { MESSAGES } from "./message-definitions";

const cardColumn = { md: { span: 4 }, sm: { span: 8 }, xxs: { span: 6 } } as const;

export const GetStarted = () => {
  const intl = useIntl();
  const {
    data: { customerDomain, customerUuid },
  } = useCustomerAppConfig();
  const { navigate: companyCreationNavigate } = useSpaNavigate("CompanyCreation");

  const { data: reviewData, isLoading: isReviewLoading } = useInReviewDetails(customerDomain);
  const createFundingApplication = useCreateFundingApplicationMutation(customerDomain, customerUuid);

  const onApply = () => {
    createFundingApplication
      .mutateAsync()
      .then(() => {
        // Todo: navigate directly to /funding/application/revenue once OBN-202 is complete
        companyCreationNavigate("revenue", { replace: true });
      })
      .catch((error) => {
        addError("Failed to create funding application:", error);
      });
  };

  if (isReviewLoading || !reviewData) {
    return (
      <LandingLayout key="loading">
        <Spinner size={32} />
      </LandingLayout>
    );
  }

  // Customer has an open product request but the funding application is not yet submitted —
  // redirect them back into the funding application flow to finish what they started.
  if (reviewData.isProductRequestInProgress && !reviewData.submittedDatetime) {
    // Todo: navigate directly to /funding/application/revenue once OBN-202 is complete
    return <Navigate to="/company-creation/revenue" replace />;
  }

  const cardProps: ApplicationStatusCardProps = reviewData.isProductRequestInProgress
    ? { state: "in-review" }
    : { state: "no-application", onApply, isApplying: createFundingApplication.isPending };

  return (
    <LandingLayout
      title={intl.formatMessage(MESSAGES.pageHeading)}
      subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
    >
      <PageGridRow>
        <PageGridCell layout={cardColumn}>
          <ApplicationStatusCard {...cardProps} />
        </PageGridCell>
      </PageGridRow>
    </LandingLayout>
  );
};
